// src/features/gift-cards/utils/giftCardTemplates.ts
// ============================================================================
// GENERADOR DE IMÁGENES DE GIFT CARD — Canvas API
// ============================================================================
//
// v2.0.0 - senderName ("De parte de:") en todos los templates
//        - 2 nuevos templates: Agradecimiento y VIP
//
// Templates disponibles:
//   1. birthday    → Cumpleaños (confetti, estrellas, colores festivos)
//   2. mothersDay  → Día de las Madres (flores, tonos rosados/dorados)
//   3. christmas   → Navidad (copos de nieve, tonos rojos/verdes/dorados)
//   4. general     → General / Lealtad (elegante, colores del negocio)
//   5. thankyou    → Agradecimiento (tonos azules/verdes, estrellas)
//   6. vip         → VIP / Exclusivo (negro y dorado, premium)
//
// Ubicación: src/features/gift-cards/utils/giftCardTemplates.ts
// ============================================================================

// ============================================================================
// TIPOS
// ============================================================================

export type GiftCardTemplateId = 'birthday' | 'mothersDay' | 'christmas' | 'general' | 'thankyou' | 'vip';

// 'custom' = diseño propio del negocio (custom_design en settings, data-driven)
export type AnyGiftCardTemplateId = GiftCardTemplateId | 'custom';

export interface GiftCardTemplateConfig {
  templateId: AnyGiftCardTemplateId;
  businessName: string;
  recipientName: string;
  points: number;
  primaryColor: string;
  logoUrl?: string | null;
  loyaltyMode?: 'visits' | 'cashback';
  senderName?: string;
  customDesign?: GiftCardCustomDesign | null;
  /** Gift card en PESOS (saldo de regalo, 14-oct-2026). Se pasa junto con loyaltyMode 'cashback' para
   *  que el monto salga «$100.00»; además el diseño propio no pinta un sufijo «puntos» detrás del monto. */
  giftMoney?: boolean;
}

export interface GiftCardTemplateInfo {
  id: AnyGiftCardTemplateId;
  label: string;
  description: string;
  icon: string;
  previewColors: [string, string];
}

// ============================================================================
// DISEÑO PROPIO DEL NEGOCIO (settings.custom_design — todo por configuración,
// cero código por tenant: fondo subido a uploads.lealify.com + campos con
// coordenadas en el espacio de píxeles del fondo)
// ============================================================================

export interface GiftCardCustomDesignField {
  key: 'recipient' | 'sender' | 'points';
  x: number;
  y: number; // borde SUPERIOR del texto (textBaseline 'top')
  size: number;
  maxWidth?: number;
  prefix?: string;
  suffix?: string;
  /** Letra de ESTE campo (por defecto la del diseño). Debe ser una de las que carga ensureCustomFont. */
  font?: 'Great Vibes' | 'Montserrat';
  weight?: number;
  /** 'center' → x es el CENTRO del texto (Irvin/Marrieto 9-oct-2026: «500 puntos» centrado). */
  align?: 'left' | 'center' | 'right';
}

export interface GiftCardCustomDesign {
  enabled: boolean;
  label?: string;
  background_url: string;
  font?: { family?: string; color?: string };
  fields: GiftCardCustomDesignField[];
}

// ============================================================================
// CATÁLOGO DE TEMPLATES (metadata para UI)
// ============================================================================

export const GIFT_CARD_TEMPLATES: GiftCardTemplateInfo[] = [
  {
    id: 'general',
    label: '🎁 General',
    description: 'Diseño elegante con los colores de tu negocio',
    icon: '🎁',
    previewColors: ['#0891b2', '#14b8a6'],
  },
  {
    id: 'birthday',
    label: '🎂 Cumpleaños',
    description: 'Celebra el cumpleaños de tu cliente con confetti y estrellas',
    icon: '🎂',
    previewColors: ['#f59e0b', '#ec4899'],
  },
  {
    id: 'mothersDay',
    label: '💐 Día de las Madres',
    description: 'Diseño floral con tonos rosados y dorados',
    icon: '💐',
    previewColors: ['#ec4899', '#f9a8d4'],
  },
  {
    id: 'christmas',
    label: '🎄 Navidad',
    description: 'Copos de nieve y colores navideños',
    icon: '🎄',
    previewColors: ['#dc2626', '#15803d'],
  },
  {
    id: 'thankyou',
    label: '🌟 Agradecimiento',
    description: 'Un detalle especial para agradecer a tus clientes',
    icon: '🌟',
    previewColors: ['#2563eb', '#7c3aed'],
  },
  {
    id: 'vip',
    label: '👑 VIP',
    description: 'Diseño premium negro y dorado para clientes especiales',
    icon: '👑',
    previewColors: ['#1c1917', '#d97706'],
  },
];

// ============================================================================
// CONSTANTES
// ============================================================================

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1080;

// ============================================================================
// HELPERS
// ============================================================================

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isColorDark = (hex: string): boolean => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
};

const darkenColor = (hex: string, amount: number): string => {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const lightenColor = (hex: string, amount: number): string => {
  const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + amount);
  const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + amount);
  const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const formatPoints = (points: number, loyaltyMode: 'visits' | 'cashback'): string => {
  if (loyaltyMode === 'cashback') return `$${points.toFixed(2)}`;
  return `${points}`;
};

const formatPointsLabel = (loyaltyMode: 'visits' | 'cashback'): string => {
  return loyaltyMode === 'cashback' ? 'de saldo' : 'puntos';
};

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`No se pudo cargar imagen: ${url}`));
    img.src = url;
  });
};

const drawCenteredText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  font: string,
  color: string,
): void => {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y, maxWidth);
};

const drawLogo = async (
  ctx: CanvasRenderingContext2D,
  logoUrl: string,
  cx: number,
  cy: number,
  radius: number,
): Promise<void> => {
  try {
    const img = await loadImage(logoUrl);
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius + 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.clip();
    const size = radius * 2;
    ctx.drawImage(img, cx - radius, cy - radius, size, size);
    ctx.restore();
  } catch {
    console.warn('No se pudo cargar el logo para la gift card');
  }
};

const drawDotPattern = (
  ctx: CanvasRenderingContext2D,
  color: string,
  density: number = 40,
): void => {
  ctx.fillStyle = hexToRgba(color, 0.08);
  for (let x = 0; x < CARD_WIDTH; x += density) {
    for (let y = 0; y < CARD_HEIGHT; y += density) {
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }
};

/**
 * Dibuja la línea "De: senderName" debajo del nombre del destinatario.
 * Solo se muestra si senderName tiene valor.
 */
const drawSenderLine = (
  ctx: CanvasRenderingContext2D,
  senderName: string | undefined,
  x: number,
  y: number,
  maxWidth: number,
  color: string,
): void => {
  if (!senderName || !senderName.trim()) return;
  drawCenteredText(
    ctx,
    `De: ${senderName.trim()}`,
    x,
    y,
    maxWidth,
    '26px "Segoe UI", sans-serif',
    color,
  );
};

// ============================================================================
// TEMPLATE: CUMPLEAÑOS 🎂
// ============================================================================

const drawBirthdayTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, primaryColor, logoUrl, loyaltyMode = 'visits', senderName } = config;

  // ── Fondo degradado cálido ──
  const bgGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  bgGrad.addColorStop(0, '#fef3c7');
  bgGrad.addColorStop(0.5, '#fde68a');
  bgGrad.addColorStop(1, '#fcd34d');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // ── Confetti esparcido ──
  const confettiColors = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
  const seed = recipientName.length + points;
  for (let i = 0; i < 65; i++) {
    const cx = ((seed * 17 + i * 137) % CARD_WIDTH);
    const cy = ((seed * 31 + i * 89) % (CARD_HEIGHT * 0.35));
    const size = 6 + ((i * 7) % 14);
    const color = confettiColors[i % confettiColors.length];
    const rotation = (i * 43) % 360;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.fillStyle = hexToRgba(color, 0.7);
    if (i % 3 === 0) {
      ctx.fillRect(-size / 2, -size / 4, size, size / 2);
    } else if (i % 3 === 1) {
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      for (let j = 0; j < 5; j++) {
        const angle = (j * 4 * Math.PI) / 5 - Math.PI / 2;
        const r = size / 2;
        ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
        const innerAngle = angle + (2 * Math.PI) / 10;
        ctx.lineTo(Math.cos(innerAngle) * (r * 0.4), Math.sin(innerAngle) * (r * 0.4));
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  for (let i = 0; i < 40; i++) {
    const cx = ((seed * 23 + i * 113) % CARD_WIDTH);
    const cy = CARD_HEIGHT * 0.75 + ((seed * 19 + i * 67) % (CARD_HEIGHT * 0.25));
    const size = 5 + ((i * 11) % 12);
    const color = confettiColors[(i + 3) % confettiColors.length];
    const rotation = (i * 67) % 360;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.fillStyle = hexToRgba(color, 0.5);
    ctx.fillRect(-size / 2, -size / 4, size, size / 2);
    ctx.restore();
  }

  // ── Tarjeta central ──
  const cardX = 90;
  const cardY = 220;
  const cardW = CARD_WIDTH - 180;
  const cardH = 620;
  const cardRadius = 32;

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 10;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
  ctx.fill();
  ctx.restore();

  drawCenteredText(ctx, '🎂', CARD_WIDTH / 2, cardY + 65, 200, '64px serif', '#000');
  drawCenteredText(ctx, '¡Feliz Cumpleaños!', CARD_WIDTH / 2, cardY + 140, cardW - 60, 'bold 52px "Segoe UI", sans-serif', '#92400e');
  drawCenteredText(ctx, recipientName, CARD_WIDTH / 2, cardY + 200, cardW - 80, '36px "Segoe UI", sans-serif', '#b45309');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 240, cardW - 80, '#d97706');

  ctx.strokeStyle = hexToRgba('#f59e0b', 0.3);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cardX + 100, cardY + 270);
  ctx.lineTo(cardX + cardW - 100, cardY + 270);
  ctx.stroke();

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 350, cardW - 60, 'bold 96px "Segoe UI", sans-serif', primaryColor);
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 410, cardW - 60, '32px "Segoe UI", sans-serif', '#78716c');

  const badgeW = 260;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 460;
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  ctx.fillStyle = primaryColor;
  ctx.fill();
  drawCenteredText(ctx, '✨ GIFT CARD ✨', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#ffffff');

  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 45, cardW - 60, 'bold 30px "Segoe UI", sans-serif', '#57534e');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 55, 40);

  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 60, CARD_WIDTH - 120, '22px "Segoe UI", sans-serif', '#a8a29e');
};

// ============================================================================
// TEMPLATE: DÍA DE LAS MADRES 💐
// ============================================================================

const drawMothersDayTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, primaryColor, logoUrl, loyaltyMode = 'visits', senderName } = config;

  const bgGrad = ctx.createLinearGradient(0, 0, 0, CARD_HEIGHT);
  bgGrad.addColorStop(0, '#fdf2f8');
  bgGrad.addColorStop(0.4, '#fce7f3');
  bgGrad.addColorStop(1, '#fbcfe8');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  drawDotPattern(ctx, '#ec4899', 35);

  const drawFlower = (cx: number, cy: number, size: number, color: string) => {
    for (let i = 0; i < 6; i++) {
      const angle = (i * 2 * Math.PI) / 6;
      const px = cx + Math.cos(angle) * size * 0.6;
      const py = cy + Math.sin(angle) * size * 0.6;
      ctx.beginPath();
      ctx.arc(px, py, size * 0.45, 0, Math.PI * 2);
      ctx.fillStyle = hexToRgba(color, 0.25);
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.25, 0, Math.PI * 2);
    ctx.fillStyle = hexToRgba('#fbbf24', 0.4);
    ctx.fill();
  };

  drawFlower(80, 100, 55, '#ec4899');
  drawFlower(180, 60, 35, '#f472b6');
  drawFlower(CARD_WIDTH - 90, 130, 50, '#ec4899');
  drawFlower(CARD_WIDTH - 180, 70, 30, '#f9a8d4');
  drawFlower(100, CARD_HEIGHT - 110, 45, '#f472b6');
  drawFlower(CARD_WIDTH - 100, CARD_HEIGHT - 90, 40, '#ec4899');
  drawFlower(CARD_WIDTH - 200, CARD_HEIGHT - 130, 30, '#f9a8d4');

  const seed = recipientName.length + points;
  for (let i = 0; i < 15; i++) {
    const x = ((seed * 13 + i * 97) % CARD_WIDTH);
    const y = ((seed * 29 + i * 73) % CARD_HEIGHT);
    const r = 3 + ((i * 7) % 8);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = hexToRgba('#d97706', 0.15);
    ctx.fill();
  }

  const cardX = 80;
  const cardY = 200;
  const cardW = CARD_WIDTH - 160;
  const cardH = 650;
  const cardRadius = 28;

  ctx.save();
  ctx.shadowColor = 'rgba(236, 72, 153, 0.15)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.strokeStyle = hexToRgba('#d97706', 0.2);
  ctx.lineWidth = 2;
  ctx.stroke();

  drawCenteredText(ctx, '💐', CARD_WIDTH / 2, cardY + 65, 200, '58px serif', '#000');
  drawCenteredText(ctx, 'Feliz Día, Mamá', CARD_WIDTH / 2, cardY + 140, cardW - 60, 'bold 50px "Segoe UI", sans-serif', '#9d174d');
  drawCenteredText(ctx, `Para: ${recipientName}`, CARD_WIDTH / 2, cardY + 200, cardW - 80, 'italic 32px "Segoe UI", sans-serif', '#be185d');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 240, cardW - 80, '#db2777');

  drawCenteredText(ctx, '✿ ─────── ✿', CARD_WIDTH / 2, cardY + 278, cardW, '28px serif', hexToRgba('#ec4899', 0.4));

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 370, cardW - 60, 'bold 92px "Segoe UI", sans-serif', primaryColor);
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 430, cardW - 60, '30px "Segoe UI", sans-serif', '#a3a3a3');

  const badgeW = 280;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 475;
  const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY);
  badgeGrad.addColorStop(0, '#ec4899');
  badgeGrad.addColorStop(1, '#f472b6');
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  ctx.fillStyle = badgeGrad;
  ctx.fill();
  drawCenteredText(ctx, '💝 GIFT CARD 💝', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#ffffff');

  drawCenteredText(ctx, 'Con todo nuestro cariño', CARD_WIDTH / 2, cardY + 565, cardW - 80, 'italic 26px "Segoe UI", sans-serif', '#db2777');
  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 40, cardW - 60, 'bold 28px "Segoe UI", sans-serif', '#6b7280');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 50, 38);
  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 55, CARD_WIDTH - 120, '21px "Segoe UI", sans-serif', '#d4d4d4');
};

// ============================================================================
// TEMPLATE: NAVIDAD 🎄
// ============================================================================

const drawChristmasTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, logoUrl, loyaltyMode = 'visits', senderName } = config;

  const bgGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  bgGrad.addColorStop(0, '#7f1d1d');
  bgGrad.addColorStop(0.5, '#1c1917');
  bgGrad.addColorStop(1, '#14532d');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  const drawSnowflake = (cx: number, cy: number, size: number, alpha: number) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.strokeStyle = hexToRgba('#ffffff', alpha);
    ctx.lineWidth = 1.5;
    for (let i = 0; i < 6; i++) {
      ctx.rotate(Math.PI / 3);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, size);
      ctx.moveTo(0, size * 0.4);
      ctx.lineTo(size * 0.25, size * 0.55);
      ctx.moveTo(0, size * 0.4);
      ctx.lineTo(-size * 0.25, size * 0.55);
      ctx.moveTo(0, size * 0.7);
      ctx.lineTo(size * 0.18, size * 0.82);
      ctx.moveTo(0, size * 0.7);
      ctx.lineTo(-size * 0.18, size * 0.82);
      ctx.stroke();
    }
    ctx.restore();
  };

  const seed = recipientName.length + points;
  for (let i = 0; i < 20; i++) {
    const x = ((seed * 17 + i * 131) % CARD_WIDTH);
    const y = ((seed * 23 + i * 97) % CARD_HEIGHT);
    const sz = 12 + ((i * 11) % 22);
    drawSnowflake(x, y, sz, 0.08 + ((i % 5) * 0.03));
  }

  for (let i = 0; i < 25; i++) {
    const x = ((seed * 19 + i * 83) % CARD_WIDTH);
    const y = ((seed * 37 + i * 61) % CARD_HEIGHT);
    const r = 1.5 + ((i * 3) % 4);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = hexToRgba('#fbbf24', 0.2 + ((i % 4) * 0.08));
    ctx.fill();
  }

  const cardX = 80;
  const cardY = 195;
  const cardW = CARD_WIDTH - 160;
  const cardH = 660;
  const cardRadius = 28;

  ctx.save();
  ctx.shadowColor = 'rgba(251, 191, 36, 0.15)';
  ctx.shadowBlur = 50;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(28, 25, 23, 0.85)';
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  const borderGrad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
  borderGrad.addColorStop(0, hexToRgba('#fbbf24', 0.5));
  borderGrad.addColorStop(0.5, hexToRgba('#f59e0b', 0.3));
  borderGrad.addColorStop(1, hexToRgba('#fbbf24', 0.5));
  ctx.strokeStyle = borderGrad;
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.beginPath();
  ctx.roundRect(cardX + 16, cardY + 16, cardW - 32, cardH - 32, 20);
  ctx.strokeStyle = hexToRgba('#fbbf24', 0.12);
  ctx.lineWidth = 1;
  ctx.stroke();

  drawCenteredText(ctx, '🎄', CARD_WIDTH / 2, cardY + 65, 200, '58px serif', '#000');
  drawCenteredText(ctx, '¡Feliz Navidad!', CARD_WIDTH / 2, cardY + 145, cardW - 60, 'bold 52px "Segoe UI", sans-serif', '#fbbf24');
  drawCenteredText(ctx, `Para: ${recipientName}`, CARD_WIDTH / 2, cardY + 205, cardW - 80, '32px "Segoe UI", sans-serif', '#d4d4d8');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 245, cardW - 80, '#a1a1aa');

  drawCenteredText(ctx, '❄ ─────── ❄', CARD_WIDTH / 2, cardY + 280, cardW, '24px serif', hexToRgba('#fbbf24', 0.35));

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 370, cardW - 60, 'bold 92px "Segoe UI", sans-serif', '#fbbf24');
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 430, cardW - 60, '30px "Segoe UI", sans-serif', '#a1a1aa');

  const badgeW = 270;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 478;
  const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY);
  badgeGrad.addColorStop(0, '#dc2626');
  badgeGrad.addColorStop(1, '#15803d');
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  ctx.fillStyle = badgeGrad;
  ctx.fill();
  drawCenteredText(ctx, '🎅 GIFT CARD 🎅', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#ffffff');

  drawCenteredText(ctx, 'Que esta temporada esté llena de alegría', CARD_WIDTH / 2, cardY + 572, cardW - 80, 'italic 24px "Segoe UI", sans-serif', '#a1a1aa');
  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 40, cardW - 60, 'bold 28px "Segoe UI", sans-serif', '#e5e7eb');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 50, 38);
  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 50, CARD_WIDTH - 120, '21px "Segoe UI", sans-serif', hexToRgba('#d4d4d8', 0.5));
};

// ============================================================================
// TEMPLATE: GENERAL 🎁
// ============================================================================

const drawGeneralTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, primaryColor, logoUrl, loyaltyMode = 'visits', senderName } = config;

  const isDark = isColorDark(primaryColor);
  const accentColor = isDark ? lightenColor(primaryColor, 60) : darkenColor(primaryColor, 60);
  const bgLight = lightenColor(primaryColor, 200);
  const bgMid = lightenColor(primaryColor, 170);

  const bgGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  bgGrad.addColorStop(0, bgLight);
  bgGrad.addColorStop(0.6, bgMid);
  bgGrad.addColorStop(1, lightenColor(primaryColor, 140));
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  drawDotPattern(ctx, primaryColor, 32);

  ctx.beginPath();
  ctx.arc(CARD_WIDTH - 60, 80, 160, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba(primaryColor, 0.07);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-40, CARD_HEIGHT / 2, 200, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba(primaryColor, 0.05);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(CARD_WIDTH + 20, CARD_HEIGHT - 100, 180, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba(primaryColor, 0.06);
  ctx.fill();

  const lineGrad = ctx.createLinearGradient(120, 0, CARD_WIDTH - 120, 0);
  lineGrad.addColorStop(0, hexToRgba(primaryColor, 0));
  lineGrad.addColorStop(0.5, hexToRgba(primaryColor, 0.3));
  lineGrad.addColorStop(1, hexToRgba(primaryColor, 0));
  ctx.strokeStyle = lineGrad;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(120, 170);
  ctx.lineTo(CARD_WIDTH - 120, 170);
  ctx.stroke();

  const cardX = 80;
  const cardY = 210;
  const cardW = CARD_WIDTH - 160;
  const cardH = 630;
  const cardRadius = 28;

  ctx.save();
  ctx.shadowColor = hexToRgba(primaryColor, 0.12);
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 10;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.strokeStyle = hexToRgba(primaryColor, 0.15);
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, 8, [cardRadius, cardRadius, 0, 0]);
  ctx.fillStyle = primaryColor;
  ctx.fill();
  ctx.restore();

  drawCenteredText(ctx, '🎁', CARD_WIDTH / 2, cardY + 60, 200, '56px serif', '#000');
  drawCenteredText(ctx, 'Gift Card', CARD_WIDTH / 2, cardY + 132, cardW - 60, 'bold 52px "Segoe UI", sans-serif', primaryColor);
  drawCenteredText(ctx, `Para: ${recipientName}`, CARD_WIDTH / 2, cardY + 192, cardW - 80, '32px "Segoe UI", sans-serif', '#6b7280');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 230, cardW - 80, '#9ca3af');

  const sepGrad = ctx.createLinearGradient(cardX + 80, 0, cardX + cardW - 80, 0);
  sepGrad.addColorStop(0, hexToRgba(primaryColor, 0));
  sepGrad.addColorStop(0.5, hexToRgba(primaryColor, 0.25));
  sepGrad.addColorStop(1, hexToRgba(primaryColor, 0));
  ctx.strokeStyle = sepGrad;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cardX + 80, cardY + 260);
  ctx.lineTo(cardX + cardW - 80, cardY + 260);
  ctx.stroke();

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 345, cardW - 60, 'bold 96px "Segoe UI", sans-serif', primaryColor);
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 405, cardW - 60, '30px "Segoe UI", sans-serif', '#9ca3af');

  const badgeW = 260;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 455;
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY);
  badgeGrad.addColorStop(0, primaryColor);
  badgeGrad.addColorStop(1, accentColor);
  ctx.fillStyle = badgeGrad;
  ctx.fill();
  drawCenteredText(ctx, '🎁 GIFT CARD 🎁', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#ffffff');

  drawCenteredText(ctx, 'Disfruta tu regalo', CARD_WIDTH / 2, cardY + 548, cardW - 80, 'italic 26px "Segoe UI", sans-serif', '#9ca3af');
  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 40, cardW - 60, 'bold 28px "Segoe UI", sans-serif', '#6b7280');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 50, 38);
  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 55, CARD_WIDTH - 120, '21px "Segoe UI", sans-serif', '#d4d4d4');
};

// ============================================================================
// TEMPLATE: AGRADECIMIENTO 🌟
// ============================================================================

const drawThankYouTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, logoUrl, loyaltyMode = 'visits', senderName } = config;

  // ── Fondo degradado azul → violeta ──
  const bgGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  bgGrad.addColorStop(0, '#eff6ff');
  bgGrad.addColorStop(0.5, '#e0e7ff');
  bgGrad.addColorStop(1, '#ede9fe');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  drawDotPattern(ctx, '#6366f1', 36);

  // ── Estrellas decorativas ──
  const seed = recipientName.length + points;
  for (let i = 0; i < 30; i++) {
    const x = ((seed * 13 + i * 107) % CARD_WIDTH);
    const y = ((seed * 29 + i * 79) % CARD_HEIGHT);
    const size = 4 + ((i * 5) % 10);
    const alpha = 0.06 + ((i % 6) * 0.02);

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(((i * 37) % 360) * Math.PI / 180);
    ctx.beginPath();
    for (let j = 0; j < 5; j++) {
      const angle = (j * 4 * Math.PI) / 5 - Math.PI / 2;
      ctx.lineTo(Math.cos(angle) * size, Math.sin(angle) * size);
      const inner = angle + (2 * Math.PI) / 10;
      ctx.lineTo(Math.cos(inner) * (size * 0.4), Math.sin(inner) * (size * 0.4));
    }
    ctx.closePath();
    ctx.fillStyle = hexToRgba('#6366f1', alpha);
    ctx.fill();
    ctx.restore();
  }

  // ── Círculos suaves ──
  ctx.beginPath();
  ctx.arc(CARD_WIDTH - 80, 120, 140, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba('#818cf8', 0.06);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(60, CARD_HEIGHT - 150, 120, 0, Math.PI * 2);
  ctx.fillStyle = hexToRgba('#a78bfa', 0.06);
  ctx.fill();

  // ── Tarjeta central ──
  const cardX = 80;
  const cardY = 210;
  const cardW = CARD_WIDTH - 160;
  const cardH = 630;
  const cardRadius = 28;

  ctx.save();
  ctx.shadowColor = 'rgba(99, 102, 241, 0.12)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 10;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.93)';
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.strokeStyle = hexToRgba('#818cf8', 0.15);
  ctx.lineWidth = 2;
  ctx.stroke();

  // Franja superior violeta
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, 8, [cardRadius, cardRadius, 0, 0]);
  const topGrad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY);
  topGrad.addColorStop(0, '#6366f1');
  topGrad.addColorStop(1, '#a78bfa');
  ctx.fillStyle = topGrad;
  ctx.fill();
  ctx.restore();

  drawCenteredText(ctx, '🌟', CARD_WIDTH / 2, cardY + 62, 200, '56px serif', '#000');
  drawCenteredText(ctx, '¡Gracias!', CARD_WIDTH / 2, cardY + 135, cardW - 60, 'bold 54px "Segoe UI", sans-serif', '#4338ca');
  drawCenteredText(ctx, `Para: ${recipientName}`, CARD_WIDTH / 2, cardY + 195, cardW - 80, '32px "Segoe UI", sans-serif', '#6366f1');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 233, cardW - 80, '#818cf8');

  // Separador
  const sepGrad = ctx.createLinearGradient(cardX + 80, 0, cardX + cardW - 80, 0);
  sepGrad.addColorStop(0, hexToRgba('#6366f1', 0));
  sepGrad.addColorStop(0.5, hexToRgba('#6366f1', 0.25));
  sepGrad.addColorStop(1, hexToRgba('#6366f1', 0));
  ctx.strokeStyle = sepGrad;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cardX + 80, cardY + 262);
  ctx.lineTo(cardX + cardW - 80, cardY + 262);
  ctx.stroke();

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 348, cardW - 60, 'bold 96px "Segoe UI", sans-serif', '#4338ca');
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 408, cardW - 60, '30px "Segoe UI", sans-serif', '#9ca3af');

  const badgeW = 260;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 458;
  const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY);
  badgeGrad.addColorStop(0, '#6366f1');
  badgeGrad.addColorStop(1, '#8b5cf6');
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  ctx.fillStyle = badgeGrad;
  ctx.fill();
  drawCenteredText(ctx, '⭐ GIFT CARD ⭐', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#ffffff');

  drawCenteredText(ctx, 'Un detalle especial para ti', CARD_WIDTH / 2, cardY + 548, cardW - 80, 'italic 26px "Segoe UI", sans-serif', '#a5b4fc');
  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 40, cardW - 60, 'bold 28px "Segoe UI", sans-serif', '#6b7280');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 50, 38);
  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 55, CARD_WIDTH - 120, '21px "Segoe UI", sans-serif', '#d4d4d4');
};

// ============================================================================
// TEMPLATE: VIP 👑
// ============================================================================

const drawVipTemplate = async (
  ctx: CanvasRenderingContext2D,
  config: GiftCardTemplateConfig,
): Promise<void> => {
  const { businessName, recipientName, points, logoUrl, loyaltyMode = 'visits', senderName } = config;

  // ── Fondo negro profundo ──
  const bgGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  bgGrad.addColorStop(0, '#0c0a09');
  bgGrad.addColorStop(0.5, '#1c1917');
  bgGrad.addColorStop(1, '#0c0a09');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // ── Brillo dorado diagonal ──
  const shineGrad = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  shineGrad.addColorStop(0, 'rgba(217, 119, 6, 0)');
  shineGrad.addColorStop(0.3, 'rgba(217, 119, 6, 0.03)');
  shineGrad.addColorStop(0.5, 'rgba(251, 191, 36, 0.06)');
  shineGrad.addColorStop(0.7, 'rgba(217, 119, 6, 0.03)');
  shineGrad.addColorStop(1, 'rgba(217, 119, 6, 0)');
  ctx.fillStyle = shineGrad;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // ── Partículas doradas ──
  const seed = recipientName.length + points;
  for (let i = 0; i < 40; i++) {
    const x = ((seed * 17 + i * 107) % CARD_WIDTH);
    const y = ((seed * 31 + i * 83) % CARD_HEIGHT);
    const r = 1 + ((i * 3) % 3);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = hexToRgba('#fbbf24', 0.08 + ((i % 5) * 0.04));
    ctx.fill();
  }

  // ── Tarjeta central ──
  const cardX = 80;
  const cardY = 195;
  const cardW = CARD_WIDTH - 160;
  const cardH = 660;
  const cardRadius = 28;

  ctx.save();
  ctx.shadowColor = 'rgba(251, 191, 36, 0.1)';
  ctx.shadowBlur = 60;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  ctx.fillStyle = 'rgba(28, 25, 23, 0.9)';
  ctx.fill();
  ctx.restore();

  // Doble borde dorado
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, cardRadius);
  const border1 = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
  border1.addColorStop(0, hexToRgba('#fbbf24', 0.6));
  border1.addColorStop(0.3, hexToRgba('#d97706', 0.2));
  border1.addColorStop(0.7, hexToRgba('#fbbf24', 0.4));
  border1.addColorStop(1, hexToRgba('#d97706', 0.6));
  ctx.strokeStyle = border1;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.roundRect(cardX + 12, cardY + 12, cardW - 24, cardH - 24, 20);
  ctx.strokeStyle = hexToRgba('#fbbf24', 0.1);
  ctx.lineWidth = 1;
  ctx.stroke();

  drawCenteredText(ctx, '👑', CARD_WIDTH / 2, cardY + 65, 200, '58px serif', '#000');
  drawCenteredText(ctx, 'Exclusivo VIP', CARD_WIDTH / 2, cardY + 145, cardW - 60, 'bold 52px "Segoe UI", sans-serif', '#fbbf24');
  drawCenteredText(ctx, `Para: ${recipientName}`, CARD_WIDTH / 2, cardY + 207, cardW - 80, '32px "Segoe UI", sans-serif', '#d4d4d8');

  // ✅ Línea "De: senderName"
  drawSenderLine(ctx, senderName, CARD_WIDTH / 2, cardY + 245, cardW - 80, '#a1a1aa');

  // Separador dorado
  drawCenteredText(ctx, '◆ ─────── ◆', CARD_WIDTH / 2, cardY + 280, cardW, '22px serif', hexToRgba('#fbbf24', 0.4));

  const pointsText = formatPoints(points, loyaltyMode);
  drawCenteredText(ctx, pointsText, CARD_WIDTH / 2, cardY + 370, cardW - 60, 'bold 96px "Segoe UI", sans-serif', '#fbbf24');
  drawCenteredText(ctx, formatPointsLabel(loyaltyMode), CARD_WIDTH / 2, cardY + 432, cardW - 60, '30px "Segoe UI", sans-serif', '#a1a1aa');

  const badgeW = 260;
  const badgeH = 48;
  const badgeX = (CARD_WIDTH - badgeW) / 2;
  const badgeY = cardY + 480;
  const badgeGrad = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeW, badgeY);
  badgeGrad.addColorStop(0, '#d97706');
  badgeGrad.addColorStop(0.5, '#fbbf24');
  badgeGrad.addColorStop(1, '#d97706');
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 24);
  ctx.fillStyle = badgeGrad;
  ctx.fill();
  drawCenteredText(ctx, '👑 GIFT CARD 👑', CARD_WIDTH / 2, badgeY + 24, badgeW - 20, 'bold 22px "Segoe UI", sans-serif', '#1c1917');

  drawCenteredText(ctx, 'Una experiencia exclusiva te espera', CARD_WIDTH / 2, cardY + 575, cardW - 80, 'italic 24px "Segoe UI", sans-serif', '#a1a1aa');
  drawCenteredText(ctx, businessName, CARD_WIDTH / 2, cardY + cardH - 40, cardW - 60, 'bold 28px "Segoe UI", sans-serif', '#e5e7eb');

  if (logoUrl) await drawLogo(ctx, logoUrl, CARD_WIDTH / 2, cardY + cardH + 50, 38);
  drawCenteredText(ctx, 'Presenta esta tarjeta en sucursal para canjear', CARD_WIDTH / 2, CARD_HEIGHT - 50, CARD_WIDTH - 120, '21px "Segoe UI", sans-serif', hexToRgba('#d4d4d8', 0.4));
};

// ============================================================================
// MAPA DE TEMPLATES
// ============================================================================

const TEMPLATE_RENDERERS: Record<
  GiftCardTemplateId,
  (ctx: CanvasRenderingContext2D, config: GiftCardTemplateConfig) => Promise<void>
> = {
  birthday: drawBirthdayTemplate,
  mothersDay: drawMothersDayTemplate,
  christmas: drawChristmasTemplate,
  general: drawGeneralTemplate,
  thankyou: drawThankYouTemplate,
  vip: drawVipTemplate,
};

// ============================================================================
// FUNCIÓN PRINCIPAL — GENERA IMAGEN BASE64
// ============================================================================

// ── Diseño propio: carga del fondo (CORS) y fuente script ──

const CUSTOM_FONT_URL = '/fonts/GreatVibes-Regular.ttf';
const DEFAULT_CUSTOM_FONT = 'Great Vibes';
const DEFAULT_CUSTOM_COLOR = '#e79cc2';

const loadBackground = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // uploads.lealify.com responde Access-Control-Allow-Origin:* para imágenes;
    // sin crossOrigin el canvas queda "tainted" y toDataURL truena.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`No se pudo cargar el fondo del diseño: ${url}`));
    img.src = url;
  });

// Fuentes que se sirven desde public/fonts (Montserrat = variable 100-900, 9-oct-2026).
const LOCAL_FONTS: Record<string, { url: string; weight?: string }> = {
  [DEFAULT_CUSTOM_FONT]: { url: CUSTOM_FONT_URL },
  Montserrat: { url: '/fonts/Montserrat.ttf', weight: '100 900' },
};
const fontReady = new Map<string, Promise<void>>();
const ensureCustomFont = (family: string): Promise<void> => {
  const local = LOCAL_FONTS[family];
  if (!local) return document.fonts.load(`32px "${family}"`).then(() => undefined);
  if (!fontReady.has(family)) {
    const ff = new FontFace(family, `url(${local.url})`, local.weight ? { weight: local.weight } : undefined);
    fontReady.set(family, ff.load().then((loaded) => { document.fonts.add(loaded); }));
  }
  return fontReady.get(family) as Promise<void>;
};

export const generateCustomGiftCardImage = async (
  design: GiftCardCustomDesign,
  config: GiftCardTemplateConfig,
): Promise<string> => {
  const bg = await loadBackground(design.background_url);
  const canvas = document.createElement('canvas');
  canvas.width = bg.naturalWidth;
  canvas.height = bg.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo crear contexto Canvas 2D');

  ctx.drawImage(bg, 0, 0);

  const family = design.font?.family || DEFAULT_CUSTOM_FONT;
  // Cada campo puede traer su propia letra (font/weight/align): se cargan todas antes de dibujar.
  await Promise.all([...new Set([family, ...design.fields.map((f) => f.font).filter(Boolean) as string[]])].map(ensureCustomFont));
  ctx.fillStyle = design.font?.color || DEFAULT_CUSTOM_COLOR;
  ctx.textBaseline = 'top';

  const loyaltyMode = config.loyaltyMode || 'visits';
  for (const f of design.fields) {
    let value = '';
    if (f.key === 'recipient') value = config.recipientName || '';
    else if (f.key === 'sender') value = config.senderName || '';
    else if (f.key === 'points') value = formatPoints(config.points, loyaltyMode);
    if (!value) continue;
    // En pesos, «$100.00 puntos» no: el sufijo que habla de puntos se omite (el arte de Marrieto lo trae).
    const suf = config.giftMoney && f.key === 'points' && /punt|pts/i.test(f.suffix || '') ? '' : (f.suffix || '');
    const text = `${f.prefix || ''}${value}${suf}`;
    ctx.textAlign = f.align || 'left';
    ctx.font = `${f.weight || 400} ${f.size}px "${f.font || family}", ${f.font === 'Montserrat' ? 'sans-serif' : 'cursive'}`;
    if (f.maxWidth) ctx.fillText(text, f.x, f.y, f.maxWidth);
    else ctx.fillText(text, f.x, f.y);
  }

  const dataUrl = canvas.toDataURL('image/png', 1.0);
  return dataUrl.split(',')[1];
};

export const generateGiftCardImage = async (
  config: GiftCardTemplateConfig,
): Promise<string> => {
  if (config.templateId === 'custom') {
    if (!config.customDesign) throw new Error('customDesign requerido para el diseño propio');
    return generateCustomGiftCardImage(config.customDesign, config);
  }

  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo crear contexto Canvas 2D');

  const renderer = TEMPLATE_RENDERERS[config.templateId];
  if (!renderer) throw new Error(`Template no encontrado: ${config.templateId}`);

  await renderer(ctx, config);

  const dataUrl = canvas.toDataURL('image/png', 1.0);
  return dataUrl.split(',')[1];
};

export const generateGiftCardPreview = async (
  config: GiftCardTemplateConfig,
): Promise<string> => {
  const base64 = await generateGiftCardImage(config);
  return `data:image/png;base64,${base64}`;
};
// ============================================================================
// IMAGEN PARA ENVIAR (9-oct-2026, Irvin/Marrieto: «Error al enviar la gift card»)
// ============================================================================
// Dos fallas que se juntaban al mandar la tarjeta al API:
//  1. Se mandaba el base64 PELADO; resolveImage() del backend (lib/whatsapp.js) solo reconoce
//     `data:<mime>;base64,…` → caía en silencio a texto sin imagen (Emme Cé ago, Oh My Glam oct).
//  2. Un diseño propio con FOTO en PNG pesa ~5 MB y express.json del API topa en 1 MB →
//     413 «entity.too.large» → el panel solo dice «Error al enviar».
// Se re-codifica a JPEG (máx. 1280 px de ancho) con su prefijo: una foto queda en ~300 KB.
const SEND_MAX_WIDTH = 1280;
const SEND_MAX_CHARS = 900_000; // margen bajo el límite de 1 MB del body JSON

export const toSendableImage = async (pngBase64: string): Promise<string> => {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('No se pudo preparar la imagen para enviar'));
    i.src = `data:image/png;base64,${pngBase64}`;
  });
  const scale = Math.min(1, SEND_MAX_WIDTH / img.naturalWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo crear contexto Canvas 2D');
  ctx.fillStyle = '#ffffff'; // JPEG no tiene transparencia: fondo blanco en vez de negro
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  let dataUrl = canvas.toDataURL('image/jpeg', 0.88);
  for (const q of [0.78, 0.68, 0.58]) {
    if (dataUrl.length <= SEND_MAX_CHARS) break;
    dataUrl = canvas.toDataURL('image/jpeg', q);
  }
  return dataUrl;
};
